import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  FormHelperText,
  InputAdornment
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
}));

const BookingEditPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [formData, setFormData] = useState({
    passengerName: '',
    passengerEmail: '',
    passengerPhone: '',
    seats: 1,
    status: 'Confirmed',
    paymentMethod: 'Carte bancaire',
    paymentStatus: 'Paid',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchBooking();
  }, [id]);

  const fetchBooking = async () => {
    setLoadingData(true);
    try {
      const bookingDoc = await getDoc(doc(firestore, 'bookings', id));
      
      if (bookingDoc.exists()) {
        const data = bookingDoc.data();
        setFormData({
          passengerName: data.passengerName || '',
          passengerEmail: data.passengerEmail || '',
          passengerPhone: data.passengerPhone || '',
          seats: data.seats || 1,
          status: data.status || 'Confirmed',
          paymentMethod: data.paymentMethod || 'Carte bancaire',
          paymentStatus: data.paymentStatus || 'Paid',
          notes: data.notes || ''
        });
      } else {
        // Données de démonstration
        setFormData({
          passengerName: 'Alice Martin',
          passengerEmail: 'alice.martin@example.com',
          passengerPhone: '+33612345678',
          seats: 2,
          status: 'Confirmed',
          paymentMethod: 'Carte bancaire',
          paymentStatus: 'Paid',
          notes: 'Réservation pour un voyage d\'affaires'
        });
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération de la réservation',
        severity: 'error'
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.passengerName.trim()) {
      newErrors.passengerName = 'Le nom du passager est requis';
    }

    if (!formData.passengerEmail.trim()) {
      newErrors.passengerEmail = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.passengerEmail)) {
      newErrors.passengerEmail = 'Email invalide';
    }

    if (!formData.passengerPhone.trim()) {
      newErrors.passengerPhone = 'Le téléphone est requis';
    }

    if (!formData.seats || formData.seats < 1) {
      newErrors.seats = 'Le nombre de places doit être au moins 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Veuillez corriger les erreurs dans le formulaire',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const bookingData = {
        ...formData,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(firestore, 'bookings', id), bookingData);

      setSnackbar({
        open: true,
        message: 'Réservation mise à jour avec succès',
        severity: 'success'
      });

      // Redirect after a short delay
      setTimeout(() => {
        navigate(`/admin/bookings/${id}`);
      }, 1500);
    } catch (error) {
      console.error('Error updating booking:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour de la réservation',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/bookings/${id}`);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loadingData) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            style={{ marginRight: '16px' }}
          >
            Retour
          </Button>
          <Typography variant="h4" component="h1">
            Modifier la réservation #{id}
          </Typography>
        </Box>

        <form className={classes.form} onSubmit={handleSubmit}>
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Informations du passager
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  className={classes.formControl}
                  label="Nom complet"
                  name="passengerName"
                  value={formData.passengerName}
                  onChange={handleChange}
                  error={Boolean(errors.passengerName)}
                  helperText={errors.passengerName}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  className={classes.formControl}
                  label="Email"
                  name="passengerEmail"
                  type="email"
                  value={formData.passengerEmail}
                  onChange={handleChange}
                  error={Boolean(errors.passengerEmail)}
                  helperText={errors.passengerEmail}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  className={classes.formControl}
                  label="Téléphone"
                  name="passengerPhone"
                  value={formData.passengerPhone}
                  onChange={handleChange}
                  error={Boolean(errors.passengerPhone)}
                  helperText={errors.passengerPhone}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  className={classes.formControl}
                  label="Nombre de places"
                  name="seats"
                  type="number"
                  value={formData.seats}
                  onChange={handleChange}
                  error={Boolean(errors.seats)}
                  helperText={errors.seats}
                  variant="outlined"
                  InputProps={{
                    inputProps: { min: 1 }
                  }}
                  required
                />
              </Grid>
            </Grid>
          </div>

          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Statut de la réservation
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl className={classes.formControl} variant="outlined">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Statut"
                  >
                    <MenuItem value="Pending">En attente</MenuItem>
                    <MenuItem value="Confirmed">Confirmée</MenuItem>
                    <MenuItem value="Completed">Terminée</MenuItem>
                    <MenuItem value="Cancelled">Annulée</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl className={classes.formControl} variant="outlined">
                  <InputLabel>Statut du paiement</InputLabel>
                  <Select
                    name="paymentStatus"
                    value={formData.paymentStatus}
                    onChange={handleChange}
                    label="Statut du paiement"
                  >
                    <MenuItem value="Pending">En attente</MenuItem>
                    <MenuItem value="Paid">Payé</MenuItem>
                    <MenuItem value="Failed">Échoué</MenuItem>
                    <MenuItem value="Refunded">Remboursé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl className={classes.formControl} variant="outlined">
                  <InputLabel>Méthode de paiement</InputLabel>
                  <Select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    label="Méthode de paiement"
                  >
                    <MenuItem value="Carte bancaire">Carte bancaire</MenuItem>
                    <MenuItem value="PayPal">PayPal</MenuItem>
                    <MenuItem value="Virement bancaire">Virement bancaire</MenuItem>
                    <MenuItem value="Espèces">Espèces</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </div>

          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Notes
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  className={classes.formControl}
                  label="Notes (optionnel)"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </div>

          <div className={classes.buttons}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.button}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </Paper>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingEditPage;
